import React, { useEffect, useState } from 'react';
import moment from 'moment';

import { CREATE, UPDATE, GET_LIST, provider, history } from '../../../_utils';
import { PcSelect } from '../../../_Components';
import Utils from '../utils';
import { fullName, isActive } from '../../format';
import '../../operators.css';

const { places, operatorsTypes, departments } = Utils.options();
const VALID_TYPES = operatorsTypes.map((item) => item.id);
const LOGIN_OPTIONAL_TYPES = ['staf'];
const LOGIN_LIKE = /^[a-z]+\d*$/i;

const needsLogin = (type) => !LOGIN_OPTIONAL_TYPES.includes(type);

const loginFromItem = (item) => {
    if (!item) return '';
    if (item.login) return String(item.login).trim();
    if (item.name && LOGIN_LIKE.test(item.name)) return String(item.name).trim();
    if (item.id && LOGIN_LIKE.test(String(item.id))) return String(item.id).trim();
    return String(item.id || item.name || '').trim();
};

const loginSortValue = (value) => {
    const digits = String(value || '').match(/\d+/);
    return digits ? parseInt(digits[0], 10) : Number.POSITIVE_INFINITY;
};

const normalizeFreeLogins = (items = []) => {
    const seen = new Set();
    return items
        .map((item) => {
            const id = loginFromItem(item);
            if (!id) return null;
            return { id, name: item.name || item.login || id };
        })
        .filter((item) => {
            if (!item || seen.has(item.id)) return false;
            seen.add(item.id);
            return true;
        })
        .sort((a, b) => {
            const byNumber = loginSortValue(a.id) - loginSortValue(b.id);
            return byNumber !== 0 ? byNumber : String(a.id).localeCompare(String(b.id));
        });
};

const withCurrentLogin = (list, currentLogin) => {
    const login = String(currentLogin || '').trim();
    if (!login || list.some((item) => item.id === login)) return list;
    return [{ id: login, name: login }, ...list];
};

const EMPTY = {
    last: '',
    first: '',
    second: '',
    type: 'operator',
    login: '',
    department: '',
    place: 'minsk',
    mobile: '',
    mobile2: '',
    telegram: '',
    email: '',
    state: '',
    district: '',
    city: '',
    street: '',
    house: '',
    block: '',
    flat: '',
    birthday: '',
    personal_number: '',
    passport_number: '',
    date_issue: '',
    issue: '',
};

const toYmd = (value) => {
    if (!value) return '';
    const date = moment(value);
    return date.isValid() ? date.format('YYYY-MM-DD') : '';
};

const digits = (value) => String(value || '').replace(/\D/g, '');

const fromOperator = (data) => {
    if (!data) return { ...EMPTY };
    const type = VALID_TYPES.includes(data.type) ? data.type : 'operator';
    return {
        last: data.name?.last || '',
        first: data.name?.first || '',
        second: data.name?.second || '',
        type,
        login: data.login || '',
        department: data.department || '',
        place: data.place || 'minsk',
        mobile: data.contact?.phone?.mobile || '',
        mobile2: data.contact?.phone?.mobile2 || '',
        telegram: data.contact?.telegram || '',
        email: data.contact?.email || '',
        state: data.contact?.address?.state || '',
        district: data.contact?.address?.district || '',
        city: data.contact?.address?.city || '',
        street: data.contact?.address?.street || '',
        house: data.contact?.address?.house || '',
        block: data.contact?.address?.block || '',
        flat: data.contact?.address?.flat || '',
        birthday: toYmd(data.passport?.birthday),
        personal_number: data.passport?.personal_number || '',
        passport_number: data.passport?.number || '',
        date_issue: toYmd(data.passport?.date_issue),
        issue: data.passport?.issue || '',
    };
};

const removeEmpty = (value) => {
    if (value === null || value === undefined || value === '') return undefined;
    if (Array.isArray(value)) {
        const next = value.map(removeEmpty).filter((item) => item !== undefined);
        return next.length ? next : undefined;
    }
    if (typeof value === 'object') {
        const next = {};
        Object.keys(value).forEach((key) => {
            const item = removeEmpty(value[key]);
            if (item !== undefined) next[key] = item;
        });
        return Object.keys(next).length ? next : undefined;
    }
    return value;
};

const Field = ({ label, span, required, error, hint, children }) => (
    <label className={`pc-emp-fl${span ? ` pc-emp-fl--${span}` : ''}${error ? ' is-invalid' : ''}`}>
        <span>
            {label}
            {required ? <i className="pc-emp-req">*</i> : null}
        </span>
        {children}
        {error ? <em className="pc-emp-ferr">{error}</em> : (hint ? <em className="pc-emp-fhint-ok">{hint}</em> : null)}
    </label>
);

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const validateOperator = (form, { freeLogins = [], freeLoginsLoading = false, currentLogin = '' } = {}) => {
    const errors = {};
    if (!String(form.last || '').trim()) errors.last = 'Укажите фамилию';
    if (!String(form.first || '').trim()) errors.first = 'Укажите имя';
    if (!form.type) errors.type = 'Укажите тип';
    const login = String(form.login || '').trim();
    if (needsLogin(form.type)) {
        if (!login) {
            errors.login = (!freeLoginsLoading && !freeLogins.length)
                ? 'Нет свободных номеров'
                : 'Укажите логин';
        } else if (!freeLoginsLoading && freeLogins.length) {
            const isCurrent = currentLogin && login === currentLogin;
            const isFree = freeLogins.some((item) => item.id === login);
            if (!isFree && !isCurrent) errors.login = 'Номер занят, выберите свободный';
        }
    }
    if (!form.department) errors.department = 'Укажите подразделение';
    if (!form.place) errors.place = 'Укажите офис';
    if (form.email && !EMAIL_RE.test(String(form.email).trim())) {
        errors.email = 'Некорректный e-mail';
    }
    if (form.birthday && moment(form.birthday).isAfter(moment(), 'day')) {
        errors.birthday = 'Дата не может быть в будущем';
    }
    if (form.date_issue && moment(form.date_issue).isAfter(moment(), 'day')) {
        errors.date_issue = 'Дата не может быть в будущем';
    }
    if (form.birthday && form.date_issue && moment(form.date_issue).isBefore(form.birthday, 'day')) {
        errors.date_issue = 'Не раньше даты рождения';
    }
    return errors;
};

const EditOperatorModal = ({ open, onClose, operatorData, onSave }) => {
    const [form, setForm] = useState(EMPTY);
    const [saving, setSaving] = useState(false);
    const [touched, setTouched] = useState({});
    const [submitted, setSubmitted] = useState(false);
    const [statusConfirm, setStatusConfirm] = useState(false);
    const [statusSaving, setStatusSaving] = useState(false);
    const [freeLogins, setFreeLogins] = useState([]);
    const [freeLoginsLoading, setFreeLoginsLoading] = useState(false);
    const [serverErrors, setServerErrors] = useState({});
    const isEdit = Boolean(operatorData);
    const currentLogin = operatorData?.login || '';
    const errors = {
        ...validateOperator(form, { freeLogins, freeLoginsLoading, currentLogin }),
        ...serverErrors,
    };
    const canSave = Object.keys(errors).length === 0 && !(needsLogin(form.type) && freeLoginsLoading);
    const showError = (key) => (submitted || touched[key]) ? errors[key] : '';
    const firstFreeLogin = freeLogins.find((item) => item.id !== currentLogin) || freeLogins[0];
    const suggestedLogin = !isEdit && needsLogin(form.type) && firstFreeLogin && form.login === firstFreeLogin.id;

    useEffect(() => {
        if (!open) return undefined;
        const initial = fromOperator(operatorData);
        setForm(initial);
        setSaving(false);
        setTouched({});
        setSubmitted(false);
        setStatusConfirm(false);
        setStatusSaving(false);
        setFreeLogins([]);
        setFreeLoginsLoading(true);
        setServerErrors({});

        let cancelled = false;
        const loadFreeLogins = async () => {
            try {
                const { data, status } = await provider(GET_LIST, '/infra/agentsFree', {
                    filter: {},
                    sort: { field: 'name', order: 'ASC' },
                    pagination: { page: 1, perPage: 999 },
                });
                if (cancelled) return;
                const list = withCurrentLogin(
                    normalizeFreeLogins(status >= 200 && status < 300 ? data : []),
                    operatorData?.login
                );
                setFreeLogins(list);
                const suggested = list.find((item) => item.id !== (operatorData?.login || '')) || list[0];
                if (suggested && needsLogin(initial.type)) {
                    setForm((prev) => (prev.login ? prev : { ...prev, login: suggested.id }));
                }
            } catch (error) {
                if (!cancelled) setFreeLogins(withCurrentLogin([], operatorData?.login));
            } finally {
                if (!cancelled) setFreeLoginsLoading(false);
            }
        };
        loadFreeLogins();

        const onKey = (event) => {
            if (event.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', onKey);
        return () => {
            cancelled = true;
            document.removeEventListener('keydown', onKey);
        };
    }, [open, operatorData, onClose]);

    if (!open) return null;

    const mark = (key) => setTouched((prev) => ({ ...prev, [key]: true }));

    const set = (key) => (event) => {
        const value = event.target.value;
        if (key === 'login' && serverErrors.login) {
            setServerErrors((prev) => {
                const next = { ...prev };
                delete next.login;
                return next;
            });
        }
        setForm((prev) => {
            const next = { ...prev, [key]: value };
            if (key === 'type' && needsLogin(value) && !String(next.login || '').trim()) {
                const suggested = freeLogins.find((item) => item.id !== currentLogin) || freeLogins[0];
                if (suggested) next.login = suggested.id;
            }
            return next;
        });
    };
    const setPhone = (key) => (event) => {
        const value = digits(event.target.value);
        setForm((prev) => ({ ...prev, [key]: value }));
    };

    const payload = () => ({
        name: {
            first: form.first,
            second: form.second,
            last: form.last,
        },
        type: form.type,
        ...(String(form.login || '').trim()
            ? { login: form.login.trim() }
            : (isEdit && !needsLogin(form.type) ? { login: '' } : {})),
        department: form.department,
        place: form.place,
        contact: {
            ...(operatorData?.contact || {}),
            phone: {
                ...(operatorData?.contact?.phone || {}),
                mobile: form.mobile || '',
                mobile2: form.mobile2 || '',
            },
            telegram: form.telegram || '',
            email: form.email || '',
            address: {
                state: form.state || '',
                district: form.district || '',
                city: form.city || '',
                street: form.street || '',
                house: form.house || '',
                block: form.block || '',
                flat: form.flat || '',
            },
        },
        passport: {
            personal_number: form.personal_number || '',
            number: form.passport_number || '',
            issue: form.issue || '',
            ...(form.date_issue ? { date_issue: form.date_issue } : {}),
            ...(form.birthday ? { birthday: form.birthday } : {}),
        },
    });

    const handleSave = async () => {
        if (saving) return;
        setSubmitted(true);
        setTouched({
            last: true,
            first: true,
            type: true,
            login: true,
            department: true,
            place: true,
            email: true,
            birthday: true,
            date_issue: true,
        });
        if (!canSave) return;
        setSaving(true);
        try {
            if (isEdit) {
                const { status, message } = await provider(UPDATE, '/operators', {
                    id: operatorData._id || operatorData.id,
                    data: payload(),
                });
                if (status === 409) {
                    setServerErrors({ login: message || 'Этот номер уже занят' });
                    return;
                }
                if (status >= 200 && status < 300) {
                    if (onSave) onSave();
                    onClose();
                }
                return;
            }

            const { status, data, message } = await provider(CREATE, '/operators', {
                data: removeEmpty({ ...payload(), status: 'Active' }),
            });
            if (status === 409) {
                setServerErrors({ login: message || 'Этот номер уже занят' });
                return;
            }
            if (status >= 200 && status < 300) {
                onClose();
                const id = data?._id || data?.id;
                if (id) history.push(`/operators/${id}/show`);
                else if (onSave) onSave();
            }
        } catch (error) {
            console.error('Error saving operator:', error);
        } finally {
            setSaving(false);
        }
    };

    const confirmStatus = async () => {
        const id = operatorData?._id || operatorData?.id;
        if (!id || statusSaving) return;
        const nextActive = !isActive(operatorData);
        setStatusSaving(true);
        try {
            const { status } = await provider(UPDATE, '/operators', {
                id,
                data: { status: nextActive ? 'Active' : 'Terminated' },
            });
            if (status >= 200 && status < 300) {
                setStatusConfirm(false);
                if (onSave) onSave();
                onClose();
            }
        } finally {
            setStatusSaving(false);
        }
    };

    return (
        <div
            className="pc-emp-ov"
            onClick={(event) => { if (event.target === event.currentTarget) onClose(); }}
        >
            <div className="pc-emp-modal pc-emp-modal--form" role="dialog" aria-modal="true" aria-labelledby="pc-emp-edit-title">
                <div className="pc-emp-mh">
                    <div>
                        <h3 id="pc-emp-edit-title">{isEdit ? 'Редактирование' : 'Новый сотрудник'}</h3>
                        {isEdit ? (
                            <div className="sub">{fullName(operatorData)}{operatorData.login ? ` · ${operatorData.login}` : ''}</div>
                        ) : (
                            <div className="sub">Карточка сотрудника</div>
                        )}
                    </div>
                    <button type="button" className="pc-emp-btn pc-emp-btn--ico pc-emp-btn--quiet" onClick={onClose} title="Закрыть">
                        <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6">
                            <path d="M4 4l8 8M12 4l-8 8" />
                        </svg>
                    </button>
                </div>

                <div className="pc-emp-mb">
                    <div className="pc-emp-form-cols">
                        <div className="pc-emp-form-col">
                            <div className="pc-emp-sect">
                                <div className="pc-emp-sect__t">Персональные данные</div>
                                <div className="pc-emp-fg pc-emp-fg--3">
                                    <Field label="Фамилия" required error={showError('last')}>
                                        <input
                                            className={`pc-emp-inp${showError('last') ? ' is-invalid' : ''}`}
                                            value={form.last}
                                            onChange={set('last')}
                                            onBlur={() => mark('last')}
                                            autoComplete="family-name"
                                        />
                                    </Field>
                                    <Field label="Имя" required error={showError('first')}>
                                        <input
                                            className={`pc-emp-inp${showError('first') ? ' is-invalid' : ''}`}
                                            value={form.first}
                                            onChange={set('first')}
                                            onBlur={() => mark('first')}
                                            autoComplete="given-name"
                                        />
                                    </Field>
                                    <Field label="Отчество">
                                        <input className="pc-emp-inp" value={form.second} onChange={set('second')} autoComplete="additional-name" />
                                    </Field>
                                </div>
                            </div>

                            <div className="pc-emp-sect">
                                <div className="pc-emp-sect__t">Основные данные</div>
                                <div className="pc-emp-fg">
                                    <Field label="Тип" required error={showError('type')}>
                                        <PcSelect
                                            className={showError('type') ? 'is-invalid' : ''}
                                            value={form.type}
                                            onChange={set('type')}
                                            onBlur={() => mark('type')}
                                            options={operatorsTypes}
                                            searchable={false}
                                        />
                                    </Field>
                                    <Field
                                        label="Логин"
                                        required={needsLogin(form.type)}
                                        error={showError('login')}
                                        hint={
                                            suggestedLogin
                                                ? 'Предложен первый свободный номер'
                                                : (!needsLogin(form.type) ? 'Для администрации можно убрать номер' : '')
                                        }
                                    >
                                        <PcSelect
                                            className={showError('login') ? 'is-invalid' : ''}
                                            value={form.login}
                                            onChange={set('login')}
                                            onBlur={() => mark('login')}
                                            disabled={freeLoginsLoading}
                                            options={freeLogins}
                                            searchable
                                            searchPlaceholder="Найти номер"
                                            allowEmpty={!needsLogin(form.type)}
                                            emptyLabel="Не указан"
                                            placeholder={
                                                freeLoginsLoading
                                                    ? 'Загрузка…'
                                                    : (needsLogin(form.type) ? 'Нет свободных номеров' : 'Не указан')
                                            }
                                            emptyText="Нет свободных номеров"
                                            mono
                                        />
                                    </Field>
                                    <Field label="Подразделение" required error={showError('department')}>
                                        <PcSelect
                                            className={showError('department') ? 'is-invalid' : ''}
                                            value={form.department}
                                            onChange={set('department')}
                                            onBlur={() => mark('department')}
                                            options={departments}
                                            allowEmpty
                                            emptyLabel="Не указано"
                                            searchable={false}
                                        />
                                    </Field>
                                    <Field label="Офис" required error={showError('place')}>
                                        <PcSelect
                                            className={showError('place') ? 'is-invalid' : ''}
                                            value={form.place}
                                            onChange={set('place')}
                                            onBlur={() => mark('place')}
                                            options={places}
                                            searchable={false}
                                        />
                                    </Field>
                                </div>
                            </div>

                            <div className="pc-emp-sect">
                                <div className="pc-emp-sect__t">Связь</div>
                                <div className="pc-emp-fg">
                                    <Field label="Мобильный">
                                        <input className="pc-emp-inp pc-emp-inp--mono" inputMode="tel" value={form.mobile} onChange={setPhone('mobile')} placeholder="37529…" />
                                    </Field>
                                    <Field label="Другой">
                                        <input className="pc-emp-inp pc-emp-inp--mono" inputMode="tel" value={form.mobile2} onChange={setPhone('mobile2')} />
                                    </Field>
                                    <Field label="Telegram">
                                        <input className="pc-emp-inp" value={form.telegram} onChange={set('telegram')} placeholder="@username" />
                                    </Field>
                                    <Field label="E-mail" error={showError('email')}>
                                        <input
                                            className={`pc-emp-inp${showError('email') ? ' is-invalid' : ''}`}
                                            type="email"
                                            value={form.email}
                                            onChange={set('email')}
                                            onBlur={() => mark('email')}
                                        />
                                    </Field>
                                </div>
                            </div>
                        </div>

                        <div className="pc-emp-form-col">
                            <div className="pc-emp-sect">
                                <div className="pc-emp-sect__t">Адрес регистрации</div>
                                <div className="pc-emp-fg">
                                    <Field label="Область">
                                        <input className="pc-emp-inp" value={form.state} onChange={set('state')} />
                                    </Field>
                                    <Field label="Район">
                                        <input className="pc-emp-inp" value={form.district} onChange={set('district')} />
                                    </Field>
                                    <Field label="Населенный пункт">
                                        <input className="pc-emp-inp" value={form.city} onChange={set('city')} />
                                    </Field>
                                    <Field label="Улица">
                                        <input className="pc-emp-inp" value={form.street} onChange={set('street')} />
                                    </Field>
                                </div>
                                <div className="pc-emp-fg pc-emp-fg--3" style={{ marginTop: 10 }}>
                                    <Field label="Дом">
                                        <input className="pc-emp-inp" value={form.house} onChange={set('house')} />
                                    </Field>
                                    <Field label="Корп.">
                                        <input className="pc-emp-inp" value={form.block} onChange={set('block')} />
                                    </Field>
                                    <Field label="Кв.">
                                        <input className="pc-emp-inp" value={form.flat} onChange={set('flat')} />
                                    </Field>
                                </div>
                            </div>

                            <div className="pc-emp-sect">
                                <div className="pc-emp-sect__t">Паспортные данные</div>
                                <div className="pc-emp-fg">
                                    <Field label="Дата рождения" error={showError('birthday')}>
                                        <input
                                            className={`pc-emp-inp pc-emp-inp--mono${showError('birthday') ? ' is-invalid' : ''}`}
                                            type="date"
                                            value={form.birthday}
                                            onChange={set('birthday')}
                                            onBlur={() => mark('birthday')}
                                        />
                                    </Field>
                                    <Field label="Личный номер">
                                        <input className="pc-emp-inp pc-emp-inp--mono" value={form.personal_number} onChange={set('personal_number')} />
                                    </Field>
                                    <Field label="Номер паспорта">
                                        <input className="pc-emp-inp pc-emp-inp--mono" value={form.passport_number} onChange={set('passport_number')} />
                                    </Field>
                                    <Field label="Дата выдачи" error={showError('date_issue')}>
                                        <input
                                            className={`pc-emp-inp pc-emp-inp--mono${showError('date_issue') ? ' is-invalid' : ''}`}
                                            type="date"
                                            value={form.date_issue}
                                            onChange={set('date_issue')}
                                            onBlur={() => mark('date_issue')}
                                        />
                                    </Field>
                                    <Field label="Орган выдачи" span={2}>
                                        <input className="pc-emp-inp" value={form.issue} onChange={set('issue')} />
                                    </Field>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="pc-emp-mf">
                    {isEdit ? (
                        <button
                            type="button"
                            className={`pc-emp-btn${isActive(operatorData) ? ' pc-emp-btn--danger' : ''}`}
                            disabled={saving || statusSaving}
                            style={{ marginRight: 'auto' }}
                            onClick={() => setStatusConfirm(true)}
                        >
                            {isActive(operatorData) ? 'Деактивировать' : 'Активировать'}
                        </button>
                    ) : null}
                    {!canSave && !freeLoginsLoading ? (
                        <span className="pc-emp-fhint">Заполните обязательные поля, отмеченные *</span>
                    ) : null}
                    <button type="button" className="pc-emp-btn pc-emp-btn--pri" disabled={saving || !canSave} onClick={handleSave}>
                        Сохранить
                    </button>
                    <button type="button" className="pc-emp-btn" onClick={onClose}>
                        Закрыть
                    </button>
                </div>
            </div>
            {statusConfirm ? (
                <div
                    className="pc-emp-ov"
                    style={{ zIndex: 401 }}
                    onClick={(event) => { if (event.target === event.currentTarget && !statusSaving) setStatusConfirm(false); }}
                >
                    <div className="pc-emp-modal pc-emp-modal--sm" role="dialog" aria-modal="true" aria-labelledby="pc-emp-status-title">
                        <div className="pc-emp-mh">
                            <div>
                                <h3 id="pc-emp-status-title">
                                    {isActive(operatorData) ? 'Деактивировать сотрудника' : 'Активировать сотрудника'}
                                </h3>
                                <div className="sub">{fullName(operatorData)}{operatorData.login ? ` · ${operatorData.login}` : ''}</div>
                            </div>
                            <button
                                type="button"
                                className="pc-emp-btn pc-emp-btn--ico pc-emp-btn--quiet"
                                disabled={statusSaving}
                                onClick={() => setStatusConfirm(false)}
                                title="Закрыть"
                            >
                                <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6">
                                    <path d="M4 4l8 8M12 4l-8 8" />
                                </svg>
                            </button>
                        </div>
                        <div className="pc-emp-mb">
                            <p className="pc-emp-note" style={{ marginTop: 0 }}>
                                {isActive(operatorData)
                                    ? 'Сотрудник станет неактивным и пропадёт из списка активных. Расчётные листы и договоры сохранятся.'
                                    : 'Сотрудник снова появится в списке активных и сможет участвовать в расчётах и сменах.'}
                            </p>
                        </div>
                        <div className="pc-emp-mf">
                            <button
                                type="button"
                                className={`pc-emp-btn${isActive(operatorData) ? ' pc-emp-btn--danger' : ' pc-emp-btn--pri'}`}
                                disabled={statusSaving}
                                onClick={confirmStatus}
                            >
                                {statusSaving
                                    ? 'Сохраняем…'
                                    : (isActive(operatorData) ? 'Деактивировать' : 'Активировать')}
                            </button>
                            <button type="button" className="pc-emp-btn" disabled={statusSaving} onClick={() => setStatusConfirm(false)}>
                                Отмена
                            </button>
                        </div>
                    </div>
                </div>
            ) : null}
        </div>
    );
};

export default EditOperatorModal;
